import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgentSupportModeChip } from './agent-support-mode-chip';
import { ChatMessages } from './chat-messages';
import { ChatInput, type ChatInputHandle } from './chat-input';
import { EmptyChatHero } from './empty-chat-hero';
import { ListeningEmptyHint } from './listening-empty-hint';
import { ComposerStarters } from './composer-starters';
import { useAgentListening } from '@/context/agent-listening-context';
import { isListeningEmptyThread, isSupportChipVisible } from '@/lib/agent-listening-chrome';
import { ComposerCapabilityMenu } from './composer-capability-menu';
import {
  getChatThreadScrollPaddingClassName,
  getComposerDockBandClassName,
  getComposerOverlayClassName,
  getInChatCapabilityStripOverlayClassName,
} from './chat-message-styles';
import { useChatThreadScrollEdges } from '@/hooks/use-chat-thread-scroll-edges';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { ChatMessage, ThreadMessageAttachment } from '@/types';
import type {
  ComposerCapabilityCategory,
  ComposerCapabilityExample,
  ComposerStarter,
  ResolvedComposerConfig,
} from '@/types/composer';
import type { ResolvedOnboardingComposer } from '@/lib/resolve-onboarding-composer';

export interface ChatPaneHandle {
  focus: () => void;
  setInput: (text: string) => void;
}

interface ChatPaneProps {
  /** Retains the composer draft when a mobile sheet unmounts; does not rerender App per keystroke. */
  draftRef?: { current: string };
  compactWelcome?: boolean;
  /** Welcome empty-chat layout when true; otherwise in-chat thread dock. See docs/DESIGN_SYSTEM.md § Chat composer modes. */
  showCenteredEmptyChat: boolean;
  agentAvailableForRoute: boolean;
  isAuthenticated: boolean;
  clientName: string;
  messages: ChatMessage[];
  isLoading: boolean;
  isPersistentConnected: boolean;
  composerConfig: ResolvedComposerConfig;
  composerStarters: ComposerStarter[];
  onboardingComposer: ResolvedOnboardingComposer;
  capabilityMenuCategories: ComposerCapabilityCategory[];
  focusKey: string | null | undefined;
  stagedAttachments: ThreadMessageAttachment[];
  chatContentBandClassName: string;
  chatSuggestionsEnabled: boolean;
  chatSuggestionsMaxPills: number;
  /** Send the composer text (guards + onBeforeSend + sendMessage in App). Returns accepted. */
  onSubmitMessage: (message: string) => boolean;
  onCancelActiveMessage: () => void;
  /** Send a capability drawer example. Returns accepted. */
  onSendCapabilityExample: (example: ComposerCapabilityExample) => boolean;
  /** Send an in-thread suggestion chip message. Returns accepted. */
  onSendSuggestion: (message: string) => boolean;
  onAttachFiles?: (files: FileList) => void;
  onRemoveStagedAttachment: (id: string) => void;
  onFeedback: (runId: string, score?: number, tags?: string[], comment?: string) => void;
  onOpenNavigationIntent: (messageId: string) => void;
  onCancelNavigationIntent: (messageId: string) => void;
}

/**
 * Owns composer-local state (input text, placeholder preview, scroll/input refs)
 * so keystrokes and drawer hovers re-render only this subtree — not AppContent.
 */
export const ChatPane = memo(
  forwardRef<ChatPaneHandle, ChatPaneProps>(function ChatPane(
    {
      draftRef,
      compactWelcome = false,
      showCenteredEmptyChat,
      agentAvailableForRoute,
      isAuthenticated,
      clientName,
      messages,
      isLoading,
      isPersistentConnected,
      composerConfig,
      composerStarters,
      onboardingComposer,
      capabilityMenuCategories,
      focusKey,
      stagedAttachments,
      chatContentBandClassName,
      chatSuggestionsEnabled,
      chatSuggestionsMaxPills,
      onSubmitMessage,
      onCancelActiveMessage,
      onSendCapabilityExample,
      onSendSuggestion,
      onAttachFiles,
      onRemoveStagedAttachment,
      onFeedback,
      onOpenNavigationIntent,
      onCancelNavigationIntent,
    },
    ref
  ) {
    const [inputText, setLocalInputText] = useState(() => draftRef?.current ?? '');
    const setInputText = useCallback((text: string) => {
      if (draftRef) draftRef.current = text;
      setLocalInputText(text);
    }, [draftRef]);
    const [placeholderPreview, setPlaceholderPreview] = useState<string | null>(null);
    const [chatPaneHovered, setChatPaneHovered] = useState(false);
    const [chatPaneFocused, setChatPaneFocused] = useState(false);
    const [capabilityDrawerOpen, setCapabilityDrawerOpen] = useState(false);
    const isMobile = useIsMobile();
    const [searchParams] = useSearchParams();
    const listening = useAgentListening();
    const chipVisible = isSupportChipVisible({
      active: listening.active,
      sessionId: listening.sessionId,
      supportThreadId: listening.supportThreadId,
      openThreadId: searchParams.get('thread'),
    });
    const listeningEmpty = isListeningEmptyThread({
      chipVisible,
      messageCount: messages.length,
    });
    const chatInputRef = useRef<ChatInputHandle>(null);
    const chatScrollRef = useRef<HTMLDivElement>(null);
    const composerBlockRef = useRef<HTMLDivElement>(null);
    const chatPaneRef = useRef<HTMLDivElement>(null);

    const showCapabilityPills =
      composerConfig.showCapabilitiesMenu && agentAvailableForRoute && !chipVisible;

    const { scrollPaddingBottom, threadMaskStyle } = useChatThreadScrollEdges(
      chatScrollRef,
      composerBlockRef,
      { active: !showCenteredEmptyChat }
    );

    useImperativeHandle(ref, () => ({
      focus: () => chatInputRef.current?.focus(),
      setInput: (text: string) => setInputText(text),
    }));

    const handleSubmit = useCallback(() => {
      if (!agentAvailableForRoute) return;
      if (isLoading) {
        onCancelActiveMessage();
        return;
      }
      if (inputText.trim() || stagedAttachments.length > 0) {
        const accepted = onSubmitMessage(inputText);
        if (accepted) setInputText('');
      }
    }, [
      agentAvailableForRoute,
      isLoading,
      inputText,
      stagedAttachments.length,
      onSubmitMessage,
      onCancelActiveMessage,
      setInputText,
    ]);

    const handlePickStarter = useCallback((message: string) => {
      setInputText(message);
      requestAnimationFrame(() => chatInputRef.current?.focus());
    }, [setInputText]);

    const handleCapabilityExampleHover = useCallback(
      (example: ComposerCapabilityExample | null) => {
        setPlaceholderPreview(
          example?.placeholder?.trim() || example?.message?.trim() || null
        );
      },
      []
    );

    const handleCapabilityExampleSelect = useCallback(
      (example: ComposerCapabilityExample) => {
        setPlaceholderPreview(null);
        const accepted = onSendCapabilityExample(example);
        if (accepted) setInputText('');
      },
      [onSendCapabilityExample, setInputText]
    );

    const syncPointerInsideChatPane = useCallback(() => {
      const pane = chatPaneRef.current;
      if (!pane) return;
      const hovered = pane.matches(':hover');
      setChatPaneHovered((prev) => (prev === hovered ? prev : hovered));
    }, []);

    // Entering in-chat layout does not fire mouseenter when the pointer is already
    // over the composer (e.g. after the first message). Sync from :hover once.
    useEffect(() => {
      if (showCenteredEmptyChat) {
        setChatPaneHovered(false);
        return;
      }
      syncPointerInsideChatPane();
      requestAnimationFrame(syncPointerInsideChatPane);
    }, [showCenteredEmptyChat, syncPointerInsideChatPane]);

    const handleChatPaneFocusIn = useCallback(() => {
      setChatPaneFocused(true);
    }, []);

    const handleChatPaneFocusOut = useCallback((event: FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget;
      if (next instanceof Node && chatPaneRef.current?.contains(next)) return;
      setChatPaneFocused(false);
    }, []);

    const handleSendSuggestion = useCallback(
      (message: string) => {
        const accepted = onSendSuggestion(message);
        if (accepted) setInputText('');
      },
      [onSendSuggestion, setInputText]
    );

    const showAttachments = agentAvailableForRoute && isAuthenticated;

    const composerPlaceholder = onboardingComposer.active
      ? onboardingComposer.placeholder ?? undefined
      : undefined;

    const capabilityMenuSharedProps = useMemo(
      () => ({
        categories: capabilityMenuCategories,
        onExampleHover: handleCapabilityExampleHover,
        onExampleSelect: handleCapabilityExampleSelect,
        disabled: isLoading,
      }),
      [
        capabilityMenuCategories,
        handleCapabilityExampleHover,
        handleCapabilityExampleSelect,
        isLoading,
      ]
    );

    const composerInputProps = useMemo(
      () => ({
        inputText,
        setInputText,
        onSubmit: handleSubmit,
        isLoading,
        clientName,
        composerPlaceholder,
        placeholderPreview,
        composerConfig,
        focusKey,
        showConnectionIndicator: agentAvailableForRoute,
        connectionConnected: isPersistentConnected,
        pendingAttachments: showAttachments ? stagedAttachments : [],
        onAttachFiles,
        onRemoveAttachment: showAttachments ? onRemoveStagedAttachment : undefined,
      }),
      [
        inputText,
        setInputText,
        handleSubmit,
        isLoading,
        clientName,
        composerPlaceholder,
        placeholderPreview,
        composerConfig,
        focusKey,
        agentAvailableForRoute,
        isPersistentConnected,
        showAttachments,
        stagedAttachments,
        onAttachFiles,
        onRemoveStagedAttachment,
      ]
    );

    const welcomeCapabilityMenuBlock = showCapabilityPills ? (
      <ComposerCapabilityMenu
        {...capabilityMenuSharedProps}
        variant="welcome"
        className="mt-2"
      />
    ) : null;

    const inChatCapabilityMenuBlock = showCapabilityPills ? (
      <ComposerCapabilityMenu
        {...capabilityMenuSharedProps}
        variant="inChat"
        onOpenChange={setCapabilityDrawerOpen}
      />
    ) : null;

    const showInChatCapabilityStrip =
      showCapabilityPills &&
      (capabilityDrawerOpen || (isMobile ? chatPaneFocused : chatPaneHovered));

    if (showCenteredEmptyChat) {
      return (
        <div className="flex min-h-0 flex-1 flex-col">
          <AgentSupportModeChip />
          <div className={cn("flex min-h-0 flex-1 flex-col items-center px-4", compactWelcome ? "justify-end py-2" : "justify-center gap-6")}>
            <div className={compactWelcome ? "hidden" : "contents"}>
            {listeningEmpty ? (
              <ListeningEmptyHint className="text-sm text-muted-foreground" />
            ) : (
              <>
            <EmptyChatHero
              greeting={onboardingComposer.active ? onboardingComposer.greeting : null}
              subline={onboardingComposer.active ? onboardingComposer.subline : null}
            />
            {composerConfig.starters !== 'none' && composerStarters.length > 0 ? (
              <ComposerStarters
                starters={composerStarters}
                onPickStarter={handlePickStarter}
                className="max-w-3xl"
              />
            ) : null}
              </>
            )}
            </div>
            <div className="w-full min-w-0 max-w-3xl mx-auto">
              <ChatInput
                ref={chatInputRef}
                {...composerInputProps}
                centered
              />
              {!compactWelcome && welcomeCapabilityMenuBlock}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={chatPaneRef}
        className="relative flex min-h-0 flex-1 flex-col"
        onMouseEnter={() => {
          if (!isMobile) setChatPaneHovered((prev) => (prev ? prev : true));
        }}
        onMouseLeave={() => {
          if (!isMobile) setChatPaneHovered((prev) => (prev ? false : prev));
        }}
        onFocusCapture={() => {
          if (isMobile) handleChatPaneFocusIn();
        }}
        onBlurCapture={(event) => {
          if (isMobile) handleChatPaneFocusOut(event);
        }}
      >
        <AgentSupportModeChip />
        <div className="relative min-h-0 flex-1" style={threadMaskStyle}>
          <div
            ref={chatScrollRef}
            className="absolute inset-y-0 left-0 right-1 overscroll-y-contain overflow-y-auto scroll-smooth touch-pan-y custom-scrollbar"
          >
            <div
              className={cn(chatContentBandClassName, getChatThreadScrollPaddingClassName())}
              style={{ paddingBottom: scrollPaddingBottom }}
            >
              {listeningEmpty ? (
                <ListeningEmptyHint className="px-1 py-8 text-sm text-muted-foreground" />
              ) : (
                <ChatMessages
                  messages={messages}
                  isStreaming={isLoading}
                  onFeedback={onFeedback}
                  onOpenNavigationIntent={onOpenNavigationIntent}
                  onCancelNavigationIntent={onCancelNavigationIntent}
                  onSendSuggestion={chatSuggestionsEnabled ? handleSendSuggestion : undefined}
                  chatSuggestionsMaxPills={chatSuggestionsMaxPills}
                  chatSuggestionsDisabled={isLoading}
                  scrollContainerRef={chatScrollRef}
                />
              )}
            </div>
          </div>
        </div>
        <div className={getComposerOverlayClassName()}>
          <div className={cn(chatContentBandClassName, getComposerDockBandClassName())}>
            <div ref={composerBlockRef} className="relative">
              {inChatCapabilityMenuBlock ? (
                <div
                  data-chat-capability-strip=""
                  className={getInChatCapabilityStripOverlayClassName(showInChatCapabilityStrip)}
                >
                  {inChatCapabilityMenuBlock}
                </div>
              ) : null}
              <ChatInput
                ref={chatInputRef}
                {...composerInputProps}
                capabilityStripAbove={showCapabilityPills}
              />
            </div>
          </div>
        </div>
      </div>
    );
  })
);
